import React, { useEffect } from "react";
import Layout from "app/companion/components/layout/Layout";
import { Container, Row, Col, Card, Button, Alert } from "react-bootstrap";
import Menu from "app/companion/pages/delivery/Menu";
import DeliveryTitle from "app/companion/pages/delivery/DeliveryTitle";
import DeliveryConfirmButton from "app/companion/pages/delivery/DeliveryConfirmButton";
import ClientSmsButton from "app/companion/pages/delivery/ClientSmsButton";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector, useDispatch } from "react-redux";
import { fetchOrder } from "state/prestationRoadSheet/actions";
import {
  faUser,
  faMapPin,
  faComment,
  faParking,
  faFileAlt,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { isUnusualRecoveryDate } from "helper/globalHelper";
import useCompanion from "app/companion/useCompanion";
import Waze from "app/universal/assets/images/waze-picto.png";
import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import RoadSheetModal from "app/companion/pages/delivery/detail/RoadSheetModal";
import DeliveryDocuments from "app/companion/pages/delivery/detail/DeliveryDocuments";
import { Link } from "react-router-dom";


// La feuille de livraison est une synthese du carnet d'adresse + info de la commande
const DeliverySheet = () => {
  const { filteredRoadmap, filteredDelivery } = useCompanion();
  const dispatch = useDispatch();
  const order = useSelector((state) => state.prestationRoadSheet.data.order);
  const isLoading = useSelector((state) => state.prestationRoadSheet.isLoading);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    if (
      filteredDelivery?.delivery?.info.order_id &&
      (!order ||
        parseInt(order.id) !==
          parseInt(filteredDelivery?.delivery?.info.order_id))
    ) {
      dispatch(
        fetchOrder(
          filteredDelivery.delivery.info.order_id,
          filteredDelivery.delivery.info.order_type
        )
      );
    }
  }, [dispatch, filteredDelivery, order]);

  let wazeUrl = "";
  if (
    filteredDelivery &&
    filteredDelivery?.delivery?.lat &&
    filteredDelivery?.delivery?.lng
  ) {
    wazeUrl = `https://waze.com/ul?ll=${filteredDelivery.delivery.lat},${filteredDelivery.delivery.lng}&navigate=no`;
  }

  const apiUrl = process.env.REACT_APP_API_URL;
  const orderId = filteredDelivery?.delivery?.info?.order_id;
  const isProdV2Order = filteredDelivery?.delivery?.info?.order_type === "v2";
  const prodToken = (orderId && isProdV2Order)
    ? window.btoa(`orderId=${orderId}`).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
    : null;
  const prodSheetUrl = prodToken ? `${apiUrl}/api/pro/orders/noauth/prod/${prodToken}.html` : null;

  const options = {
    streetViewControl: false, // Désactiver le contrôle Street View
    mapTypeControl: true,
    fullscreenControl: false
  };
  
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    return phone.replace(/\D/g, '').slice(-10);
  };

  const recoveryDate = order?.delivery?.recovery_date || filteredDelivery?.delivery?.recovery_date;
  const hasUnusualRecoveryDate = filteredDelivery?.recovery
    && isUnusualRecoveryDate(
      filteredDelivery.delivery.delivery_date,
      recoveryDate
    );

  // Indicateur "récupération programmée / non programmée" : la presta attend une récup
  // (delivery.recovery) et on est sur l'arrêt de livraison, pas sur l'arrêt de récup lui-même.
  // recovery_scheduled_date = date de la tournée qui porte l'arrêt de recup (null = non programmée).
  const expectsRecovery =
    !filteredDelivery?.recovery && Boolean(filteredDelivery?.delivery?.recovery);
  const recoveryScheduledDate = filteredDelivery?.delivery?.recovery_scheduled_date;
  
  return (
    (filteredDelivery &&
    filteredRoadmap) ? (
      <Layout
        backButton
        date={moment(filteredDelivery.delivery.delivery_date).format(
          "YYYY-MM-DD"
        )}
      >
        <>
          <Container fluid={true}>
            <>
              <DeliveryTitle/>
              <h5 className="text-center my-3">FEUILLE DE LIVRAISON</h5>
              {hasUnusualRecoveryDate && (
                <Alert variant="warning" className="text-center py-2 mb-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
                  <strong>Récupération pas le lendemain</strong>
                  <br />
                  <small>
                    Livraison le {moment(filteredDelivery.delivery.delivery_date).format("DD/MM/YYYY")}
                    {" — "}
                    récup. prévue le {moment(recoveryDate).format("DD/MM/YYYY")}
                  </small>
                </Alert>
              )}
              <hr />
              <APIProvider apiKey={googleMapsApiKey}>
                <Map
                  mapId="8f0b8aa9cd5906b"
                  style={{ width: "100%", height: "200px" }}
                  defaultCenter={{
                    lat: filteredDelivery?.delivery?.lat || 0,
                    lng: filteredDelivery?.delivery?.lng || 0,
                  }}
                  defaultZoom={15}
                  scrollwheel={false}
                  options={options}
                >
                  <AdvancedMarker
                    position={{
                      lat: filteredDelivery?.delivery?.lat || 0,
                      lng: filteredDelivery?.delivery?.lng || 0,
                    }}
                  />
                </Map>
              </APIProvider>
              <Card className="mt-2">
                <Card.Body>
                  <Row className="align-items-center" noGutters>
                    <Col xs={filteredDelivery.recovery ? 6 : 4} style={{ borderRight: "1px solid #ddd", textAlign: "center" }}>
                      <small>
                        {filteredDelivery.recovery ? (
                          <>
                            <b>{order?.prestation_type_fr}</b>
                            <br />
                            {filteredDelivery.delivery.info.numbering} du{" "}
                            {moment(
                              filteredDelivery.delivery.delivery_date
                            ).format("D/M/Y")}
                            {filteredDelivery?.delivery?.delivery_delivery_man && <>
                              <br />livré par {filteredDelivery.delivery.delivery_delivery_man.firstname}</>}
                          </>
                        ) : (
                          <>
                            <b>{order?.prestation_type_fr}</b>
                            <br />
                            {filteredDelivery.delivery.person} pers
                          </>
                        )}
                        <br />
                        {order?.delivery?.blanket
                          ? "Avec Nappe"
                          : "Sans nappe"}
                        
                        {order?.delivery?.service && 
                          " / Avec service "
                        }
                      </small>
                    </Col>
                    {!filteredDelivery.recovery && (
                      <Col
                        xs={4}
                        style={{
                          textAlign: "center",
                          borderRight: "1px solid #ddd",
                        }}
                      >
                        <small>
                          Arrivée du Moulin entre :<br />
                          <b>
                            {order?.delivery?.arrived_at_start
                              ? moment(
                                  order?.delivery?.arrived_at_start
                                ).format("HH:mm")
                              : "NC"}{" "}
                            et{" "}
                            {order?.delivery?.arrived_at_end
                              ? moment(order?.delivery?.arrived_at_end).format(
                                  "HH:mm"
                                )
                              : "NC"}
                          </b>
                        </small>
                      </Col>
                    )}
                    {!filteredDelivery.recovery && (
                      <Col
                        xs={4}
                        style={{
                          textAlign: "center",
                          borderRight: "1px solid #ddd",
                        }}
                      >
                        <small>
                          Début événement :<br />{" "}
                          <b>
                            {order?.delivery?.start_at
                              ? moment(order?.delivery?.start_at).format(
                                  "HH:mm"
                                )
                              : "NC"}
                          </b>
                        </small>
                      </Col>
                    )}
                    {filteredDelivery.recovery && (
                      <Col xs={6} style={{ textAlign: "center" }}>
                        <small
                          className={hasUnusualRecoveryDate ? "p-2 d-block bg-warning text-dark rounded border border-warning" : undefined}
                        >
                          {filteredDelivery.delivery.person} pers<br/>
                          {hasUnusualRecoveryDate && (
                            <>
                              <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
                            </>
                          )}
                          Récup. prévue :<br />{" "}
                          <b>
                            {recoveryDate
                              ? moment(recoveryDate).format("DD/MM/YYYY")
                              : "NC"}
                          </b>
                          {hasUnusualRecoveryDate && (
                            <>
                              <br />
                              <span className="font-weight-bold" style={{ fontSize: "0.75rem" }}>
                                Livraison le {moment(filteredDelivery.delivery.delivery_date).format("DD/MM/YYYY")}
                              </span>
                            </>
                          )}
                          {(order?.delivery?.recovery_start_at || order?.delivery?.recovery_end_at) && (
                            <>
                              <br />
                              {order?.delivery?.recovery_start_at ? moment(order?.delivery?.recovery_start_at).format("HH:mm") : "?"}
                              {" – "}
                              {order?.delivery?.recovery_end_at ? moment(order?.delivery?.recovery_end_at).format("HH:mm") : "?"}
                            </>
                          )}
                        </small>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mt-2">
                <Card.Body>
                  <Row className="align-items-center" noGutters>
                    <Col xs={1}>
                      <FontAwesomeIcon icon={faUser} />
                    </Col>
                    <Col xs={11}>
                      {filteredDelivery.delivery.company ? filteredDelivery.delivery.company : "Société : NC"}
                      <br />
                      {filteredDelivery.delivery.firstname ? filteredDelivery.delivery.firstname : "Prénom : NC, "} {filteredDelivery.delivery.lastname ? filteredDelivery.delivery.lastname : "Nom : NC"}
                      <br />
                      
                      {filteredDelivery?.delivery?.phone &&
                        (<a href={`tel:${formatPhoneNumber(filteredDelivery.delivery.phone)}`}>{filteredDelivery.delivery.phone}</a>)
                      }
                      {filteredDelivery?.delivery?.phone && filteredDelivery?.delivery?.phone_secondary &&
                        <span className="mx-1">/</span>
                      }
                      {filteredDelivery?.delivery?.phone_secondary &&
                        <a href={`tel:${formatPhoneNumber(filteredDelivery.delivery.phone_secondary)}`}>{filteredDelivery.delivery.phone_secondary}</a>
                      }
                      {filteredDelivery?.delivery?.phone && filteredDelivery?.delivery?.phone_secondary &&
                        <br />
                      }
                      <br/><b>NUMERO UTILE :</b>
                      <br />
                      {order?.delivery?.delivery_address_book?.useful_number ||
                        "NC"}
                      <ClientSmsButton delivery={filteredDelivery} />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mt-2">
                <Card.Body>
                  <Row noGutters>
                    <Col xs={1}>
                      <FontAwesomeIcon icon={faMapPin} />
                    </Col>
                    <Col>
                      <b>{filteredDelivery.delivery.company}</b><br />
                      {filteredDelivery.delivery.street_number}{" "}
                      {filteredDelivery.delivery.street ? filteredDelivery.delivery.street : "Rue : NC, "}
                      <br />
                      {filteredDelivery.delivery.postal_code ? filteredDelivery.delivery.postal_code : "Code postal : NC, "}{" "}
                      {filteredDelivery.delivery.city ? filteredDelivery.delivery.city : "Ville : NC"}
                      <br />
                      {filteredDelivery.delivery.address_complement}
                    </Col>
                    <Col className="text-right" style={{ width: "70px" }}>
                      <Button
                        href={wazeUrl}
                        className="waze-button"
                        variant="outline-info"
                        target="_blank"
                      >
                        <img src={Waze} />
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mt-2">
                <Card.Body>
                  <Row noGutters>
                    <Col xs={1}>
                      <FontAwesomeIcon icon={faComment} />
                    </Col>
                    <Col xs={11}>
                      <b>LIVRAISON :</b>
                      <br />
                      {order?.delivery?.delivery_information
                        ? order?.delivery?.delivery_information
                        : "NC"}
                      <br />
                      <>
                        <b>RÉCUPÉRATION :</b>
                        <br />
                        {expectsRecovery && (
                          <>
                            {recoveryScheduledDate ? (
                              <span className="text-success font-weight-bold">
                                Programmée le{" "}
                                {moment(recoveryScheduledDate).format("DD/MM/YYYY")}
                              </span>
                            ) : (
                              <span className="text-danger font-weight-bold">
                                <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
                                Non programmée
                              </span>
                            )}
                            <br />
                          </>
                        )}
                        {order?.delivery?.recover_information
                          ? order?.delivery?.recover_information
                          : "NC"}
                      </>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mt-2">
                <Card.Body>
                  <Row noGutters>
                    <Col xs={1}>
                      <FontAwesomeIcon icon={faParking} />
                    </Col>
                    <Col xs={11}>
                      <b>PARKING</b>
                      <br />
                      {order?.delivery?.delivery_address_book
                        ?.parking_information || "NC"}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              {prodSheetUrl && (
                <Card className="mt-2">
                  <Card.Body>
                    <Row noGutters>
                      <Col xs={1}>
                        <FontAwesomeIcon icon={faFileAlt} />
                      </Col>
                      <Col xs={11}>
                        <b>DEVIS PROD</b>
                        <br />
                        <a href={prodSheetUrl} target="_blank" rel="noopener noreferrer">
                          Ouvrir la fiche de production (lecture seule)
                        </a>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
              {order && (
                <DeliveryDocuments
                  order={order}
                  groupedOrderId={filteredDelivery?.delivery?.info?.grouped_order_id}
                />
              )}
              {order?.delivery?.delivery_address_book?.delivery_address_medias.map(
                (media, index) => (
                  <Card className="mt-2" key={index}>
                    <Card.Body>
                      <Row noGutters>
                        <Col xs={12}>
                          <b>{media.title}</b>
                          <br />
                          <img
                            src={`${apiUrl}/${media.media.path}`}
                            alt={media.title}
                            style={{ width: "100%" }}
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )
              )}
              <br />
              {/* Lorsqu'on a du service alors on affiche la feuille de route en complément */}
              {order?.delivery?.service && (
                <RoadSheetModal
                  order={order}
                  rmDelivery={filteredDelivery}
                  roadmap={filteredRoadmap}
                  isLoading={isLoading}
                />
              )}
            </>
          </Container>
          <DeliveryConfirmButton />
          <Menu
            roadmap={filteredRoadmap && filteredRoadmap}
            delivery={filteredDelivery && filteredDelivery}
            comments={filteredDelivery?.delivery?.comments?.length}
          />
        </>
      </Layout>
    )
    :
    (
      <p>Cette livraison n'existe pas.
        <Link to={`/companion`} style={{ textDecoration: 'none' }}>
          <Button variant="secondary" className="mt-3" block>
              Retourner à l'accueil
          </Button>
        </Link>
      </p>
    )
  );
};

export default DeliverySheet;
